import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Sparkles, Copy, Archive, ArchiveRestore, Edit, BarChart3, QrCode, ExternalLink, ToggleLeft, ToggleRight, Eye, EyeOff, ShieldCheck, ShieldOff, KeyRound, Crown, RotateCw } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import LanguageToggle from "../components/LanguageToggle";
import QRCodeModal from "../components/QRCodeModal";
import AdminShell from "../components/AdminShell";
import ImageUploader from "../components/ImageUploader";
import { useAdminAuth } from "../auth/AdminAuthContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

export default function AdminEventsList() {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const { logout: doLogout } = useAdminAuth();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tokenDraft, setTokenDraft] = useState("");
  const [loginError, setLoginError] = useState("");
  const [events, setEvents] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    name: "", slug: "", event_date: "", event_time: "", ends_at_time: "",
    location: "", address: "", venue_name: "", hero_image_url: "",
  });
  const [qrSlug, setQrSlug] = useState(null);
  const [toast, setToast] = useState("");

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const load = async () => {
    if (!token) return;
    try {
      const r = await api.get("/admin/nail-events", { headers, params: { include_archived: includeArchived } });
      setEvents(r.data.events);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken("");
        sessionStorage.removeItem(TOKEN_KEY);
        setLoginError(t("admin.login_error"));
      }
    }
  };

  useEffect(() => { load(); }, [token, includeArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await api.get("/admin/nail-events", { headers: { "X-Admin-Token": tokenDraft } });
      setToken(tokenDraft);
      sessionStorage.setItem(TOKEN_KEY, tokenDraft);
    } catch (err) {
      setLoginError(err?.response?.status === 401 ? t("admin.login_error") : err?.message || "Error");
    }
  };

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2200); };

  const createEvent = async (e) => {
    e.preventDefault();
    if (!draft.name.trim() || !draft.event_date.trim() || !draft.location.trim()) return;
    try {
      // Compose event_time from start + end (if end provided).
      const timeString = draft.event_time.trim()
        ? (draft.ends_at_time.trim() ? `${draft.event_time.trim()} – ${draft.ends_at_time.trim()}` : draft.event_time.trim())
        : "";

      // Step 1: create bare event.
      const r = await api.post("/admin/nail-events", {
        name: draft.name.trim(),
        slug: draft.slug.trim() || undefined,
        event_date: draft.event_date,
        location: draft.location.trim(),
      }, { headers });

      // Step 2: patch extra fields (address, time, flyer) via PUT — the create
      // endpoint only takes the 4 required columns.
      const patch = {};
      if (timeString) patch.event_time = timeString;
      if (draft.address.trim()) patch.address = draft.address.trim();
      if (draft.venue_name.trim()) patch.venue_name = draft.venue_name.trim();
      if (draft.hero_image_url.trim()) patch.hero_image_url = draft.hero_image_url.trim();
      if (Object.keys(patch).length > 0) {
        try {
          await api.put(`/admin/nail-events/${r.data.slug}`, patch, { headers });
        } catch (_e) { /* non-fatal; user can edit later */ }
      }

      flash(lang === "en" ? "Event created" : "Đã tạo sự kiện");
      navigate(`/admin/nail-events/${r.data.slug}/edit`);
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    }
  };

  const duplicate = async (slug) => {
    try {
      const r = await api.post(`/admin/nail-events/${slug}/duplicate`, {}, { headers });
      flash(lang === "en" ? "Duplicated" : "Đã sao chép");
      navigate(`/admin/nail-events/${r.data.slug}/edit`);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const archive = async (slug, archived) => {
    try {
      await api.post(`/admin/nail-events/${slug}/archive`, {}, { headers, params: { archived } });
      flash(archived ? (lang === "en" ? "Archived" : "Đã lưu trữ") : (lang === "en" ? "Restored" : "Đã khôi phục"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (slug, active) => {
    try {
      await api.post(`/admin/nail-events/${slug}/activate`, {}, { headers, params: { active } });
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const copyLink = async (url, label) => {
    try { await navigator.clipboard.writeText(url); flash(`${label}: ${t("admin.copied")}`); }
    catch { window.prompt(label, url); }
  };

  const qrLinksFor = (slug) => {
    const origin = window.location.origin;
    return [
      { key: "public", label: lang === "en" ? "Public Signup" : "Đăng ký", url: `${origin}/nail-event/${slug}` },
      { key: "checkin", label: lang === "en" ? "Check-in" : "Check-in", url: `${origin}/nail-event/${slug}/checkin` },
      { key: "facebook", label: "Facebook", url: `${origin}/nail-event/${slug}?utm_source=facebook&utm_medium=social&utm_campaign=${slug}` },
      { key: "instagram", label: "Instagram", url: `${origin}/nail-event/${slug}?utm_source=instagram&utm_medium=social&utm_campaign=${slug}` },
      { key: "flyer", label: lang === "en" ? "Flyer" : "Tờ rơi", url: `${origin}/nail-event/${slug}?utm_source=flyer&utm_medium=print&utm_campaign=${slug}` },
      { key: "agent", label: lang === "en" ? "Agent" : "Đại lý", url: `${origin}/nail-event/${slug}?source=agent` },
    ];
  };

  if (!token) {
    return <Login tokenDraft={tokenDraft} setTokenDraft={setTokenDraft} loginError={loginError} onSubmit={handleLogin} />;
  }

  return (
    <AdminShell
      testid="admin-events-list"
      subtitle={lang === "en" ? "Platform Admin" : "Quản Trị Platform"}
      title={lang === "en" ? "All Events" : "Tất Cả Sự Kiện"}
      actions={
        <label className="hidden sm:inline-flex items-center gap-2 text-xs text-espresso-soft">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="rounded" data-testid="include-archived-toggle" />
          {lang === "en" ? "Show archived" : "Hiện đã lưu trữ"}
        </label>
      }
    >
      <div className="max-w-[1400px] space-y-5 sm:space-y-6">
        {toast && <div data-testid="admin-toast" className="bg-[#E1EDE6] text-[#2D4A3E] inline-block rounded-xl px-4 py-2 text-sm font-medium">{toast}</div>}

        {/* Create card */}
        <div className="bg-white border border-espresso/10 rounded-3xl p-5 sm:p-6">
          <button type="button" onClick={() => setCreating((c) => !c)} data-testid="create-event-toggle" className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2.5 text-sm font-medium hover:bg-espresso-hover transition-all">
            <Plus className="w-4 h-4" /> {lang === "en" ? "Create event" : "Tạo sự kiện"}
          </button>
          {creating && (
            <form onSubmit={createEvent} className="mt-5 space-y-4" data-testid="create-event-form">
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                <SmallField label={lang === "en" ? "Event name *" : "Tên sự kiện *"} required value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} testId="new-event-name" />
                <SmallField label={lang === "en" ? "URL slug (optional)" : "URL slug (tùy chọn)"} value={draft.slug} onChange={(v) => setDraft((d) => ({ ...d, slug: v }))} placeholder="auto-generated-if-blank" testId="new-event-slug" />
                <SmallField label={lang === "en" ? "Date * (YYYY-MM-DD)" : "Ngày * (YYYY-MM-DD)"} required type="date" value={draft.event_date} onChange={(v) => setDraft((d) => ({ ...d, event_date: v }))} placeholder="2026-08-11" testId="new-event-date" />
                <SmallField label={lang === "en" ? "Start time" : "Giờ bắt đầu"} type="time" value={draft.event_time} onChange={(v) => setDraft((d) => ({ ...d, event_time: v }))} placeholder="18:30" testId="new-event-start" />
                <SmallField label={lang === "en" ? "End time" : "Giờ kết thúc"} type="time" value={draft.ends_at_time} onChange={(v) => setDraft((d) => ({ ...d, ends_at_time: v }))} placeholder="20:30" testId="new-event-end" />
                <SmallField label={lang === "en" ? "Venue name" : "Tên địa điểm"} value={draft.venue_name} onChange={(v) => setDraft((d) => ({ ...d, venue_name: v }))} placeholder="Marriott Ballroom" testId="new-event-venue" />
                <SmallField label={lang === "en" ? "City / area *" : "Thành phố *"} required value={draft.location} onChange={(v) => setDraft((d) => ({ ...d, location: v }))} placeholder="Manteca, CA" testId="new-event-location" />
                <div className="sm:col-span-2">
                  <SmallField label={lang === "en" ? "Street address" : "Địa chỉ đầy đủ"} value={draft.address} onChange={(v) => setDraft((d) => ({ ...d, address: v }))} placeholder="2111 Moffat Blvd, Manteca, CA 95336" testId="new-event-address" />
                </div>
              </div>

              {/* Flyer / banner uploader — real upload to DigitalOcean Spaces */}
              <ImageUploader
                label={lang === "en" ? "Event flyer / banner" : "Flyer / banner sự kiện"}
                value={draft.hero_image_url}
                onChange={(url) => setDraft((d) => ({ ...d, hero_image_url: url }))}
                subfolder="events"
                aspect="portrait"
                testId="new-event-flyer"
                hint={lang === "en"
                  ? "Portrait poster (4:5) works best. Max 10MB. Stored in DigitalOcean Spaces."
                  : "Ảnh chân dung (4:5) hiển thị đẹp nhất. Tối đa 10MB. Lưu tại DigitalOcean Spaces."}
              />

              <button type="submit" data-testid="new-event-submit" className="w-full sm:w-auto inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-6 py-2.5 text-sm font-medium hover:bg-espresso-hover">
                <Plus className="w-4 h-4" /> {lang === "en" ? "Create event" : "Tạo sự kiện"}
              </button>
            </form>
          )}
        </div>

        {/* Events grid */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {events.length === 0 && (
            <div className="col-span-full text-center text-espresso-muted py-12 border border-dashed border-espresso/20 rounded-3xl">
              {lang === "en" ? "No events yet. Create one above." : "Chưa có sự kiện nào. Hãy tạo mới ở trên."}
            </div>
          )}
          {events.map((ev) => (
            <div key={ev.id} className="bg-white border border-espresso/10 rounded-3xl overflow-hidden flex flex-col" data-testid={`event-card-${ev.slug}`}>
              <div className="aspect-[2/1] bg-linen-100 overflow-hidden">
                {ev.hero_image_url ? (
                  <img src={ev.hero_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-espresso-muted font-serif text-3xl">{ev.name?.[0]}</div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-serif text-2xl text-espresso truncate">{ev.name}</h3>
                    <div className="text-espresso-soft text-sm mt-1">{ev.event_date} · {ev.city || ev.location}</div>
                  </div>
                  <Badge active={ev.active} archived={ev.archived} lang={lang} />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <Stat label={lang === "en" ? "Signups" : "Đăng ký"} value={ev.stats?.total || 0} />
                  <Stat label={lang === "en" ? "Checked" : "Check-in"} value={ev.stats?.checked || 0} tone="success" />
                  <Stat label={lang === "en" ? "Walk-ins" : "Walk-in"} value={ev.stats?.walkins || 0} />
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link to={`/admin/nail-events/${ev.slug}`} data-testid={`event-dashboard-${ev.slug}`} className="inline-flex items-center gap-1 bg-espresso text-linen rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-espresso-hover">
                    <BarChart3 className="w-3.5 h-3.5" /> {lang === "en" ? "Dashboard" : "Thống kê"}
                  </Link>
                  <Link to={`/admin/nail-events/${ev.slug}/edit`} data-testid={`event-edit-${ev.slug}`} className="inline-flex items-center gap-1 border border-espresso/20 text-espresso rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-linen-200">
                    <Edit className="w-3.5 h-3.5" /> {lang === "en" ? "Edit" : "Chỉnh sửa"}
                  </Link>
                  <button type="button" onClick={() => setQrSlug(ev.slug)} data-testid={`event-qr-${ev.slug}`} className="inline-flex items-center gap-1 border border-espresso/20 text-espresso rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-linen-200">
                    <QrCode className="w-3.5 h-3.5" /> QR
                  </button>
                  <button type="button" onClick={() => duplicate(ev.slug)} data-testid={`event-duplicate-${ev.slug}`} className="inline-flex items-center gap-1 border border-espresso/20 text-espresso-soft rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-linen-200">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => toggleActive(ev.slug, !ev.active)} data-testid={`event-toggle-${ev.slug}`} className="inline-flex items-center gap-1 border border-espresso/20 text-espresso-soft rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-linen-200">
                    {ev.active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                  </button>
                  <button type="button" onClick={() => archive(ev.slug, !ev.archived)} data-testid={`event-archive-${ev.slug}`} className="inline-flex items-center gap-1 border border-espresso/20 text-espresso-soft rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-linen-200">
                    {ev.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <KioskWidget ev={ev} lang={lang} onCopy={copyLink} token={token} flash={flash} onRefresh={load} />
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => copyLink(`${window.location.origin}/nail-event/${ev.slug}`, "Public")} data-testid={`copy-public-${ev.slug}`} className="text-[11px] uppercase tracking-[0.18em] text-espresso-muted hover:text-espresso inline-flex items-center gap-1.5 justify-start">
                    <Copy className="w-3 h-3" /> {lang === "en" ? "Copy public link" : "Link đăng ký"}
                  </button>
                  <a href={`/nail-event/${ev.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`open-public-${ev.slug}`} className="text-[11px] uppercase tracking-[0.18em] text-espresso-muted hover:text-espresso inline-flex items-center gap-1.5 justify-end">
                    <ExternalLink className="w-3 h-3" /> {lang === "en" ? "Open" : "Mở"}
                  </a>
              </div>
            </div>
          </div>
          ))}
        </div>
      </div>

      <QRCodeModal open={!!qrSlug} onClose={() => setQrSlug(null)} links={qrSlug ? qrLinksFor(qrSlug) : []} />
    </AdminShell>
  );
}

function Login({ tokenDraft, setTokenDraft, loginError, onSubmit }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-6">
      <div className="absolute top-6 right-6"><LanguageToggle /></div>
      <form onSubmit={onSubmit} className="w-full max-w-md bg-white border border-espresso/10 rounded-3xl p-8 md:p-10 shadow-[0_24px_60px_-30px_rgba(44,36,27,0.35)]">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-4 h-4 text-rose-accent" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">{t("admin.title")}</span>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-espresso">{t("admin.login_title")}</h2>
        <p className="text-espresso-soft mt-2">{t("admin.login_subtitle")}</p>
        <input type="password" autoFocus data-testid="admin-token-input" value={tokenDraft} onChange={(e) => setTokenDraft(e.target.value)} placeholder={t("admin.login_placeholder")}
          className="mt-6 w-full bg-white border border-espresso/10 rounded-2xl px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-espresso focus:border-espresso" />
        {loginError && <p data-testid="admin-login-error" className="mt-3 text-sm text-[#7A2E2E]">{loginError}</p>}
        <button type="submit" data-testid="admin-login-submit" className="mt-6 w-full bg-espresso text-linen rounded-full px-6 py-4 font-medium hover:bg-espresso-hover transition-all">
          {t("admin.login_submit")}
        </button>
      </form>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="bg-linen-100 rounded-2xl py-3">
      <div className={`font-serif text-2xl ${tone === "success" ? "text-[#2D4A3E]" : "text-espresso"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted mt-1 font-semibold">{label}</div>
    </div>
  );
}

function Badge({ active, archived, lang }) {
  if (archived) return <span className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#EAE5D9] text-espresso-soft">{lang === "en" ? "Archived" : "Lưu trữ"}</span>;
  if (active) return <span className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#E1EDE6] text-[#2D4A3E]">{lang === "en" ? "Live" : "Đang chạy"}</span>;
  return <span className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#FCEBD4] text-[#8A5A19]">{lang === "en" ? "Draft" : "Bản nháp"}</span>;
}

function SmallField({ label, value, onChange, required, placeholder, testId, type }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">{label} {required && <span className="text-rose-accent">*</span>}</span>
      <input data-testid={testId} type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required}
        className="mt-1 w-full bg-white border border-espresso/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
    </label>
  );
}

function KioskWidget({ ev, lang, onCopy, token, flash, onRefresh }) {
  const [revealed, setRevealed] = useState(false);
  const [creds, setCreds] = useState(null); // {pin, link_token, checkin_url, rotated_at}
  const [rotating, setRotating] = useState(false);
  const enabled = ev.kiosk_enabled !== false;

  useEffect(() => {
    if (!token || !ev.slug) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/nail-events/${ev.slug}/kiosk-credentials`, {
          headers: { "X-Admin-Token": token },
        });
        if (!cancelled) setCreds(r.data);
      } catch {
        // Fail quietly — the widget still shows what's on the event row.
      }
    })();
    return () => { cancelled = true; };
  }, [token, ev.slug]);

  const pin = creds?.pin || ev.checkin_pin || "";
  const linkToken = creds?.link_token || "";
  const code = ev.event_kiosk_code || "—";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const checkinUrl = linkToken
    ? `${origin}/nail-event/${ev.slug}/checkin?k=${encodeURIComponent(linkToken)}`
    : `${origin}/nail-event/${ev.slug}/checkin`;
  const registrationUrl = `${origin}/nail-event/${ev.slug}`;

  const copyLinkAndPin = () => {
    const label = lang === "en" ? "Check-in link + PIN" : "Link check-in + PIN";
    const msg = lang === "en"
      ? `Kiosk check-in for ${ev.name}\nLink: ${checkinUrl}\nPIN: ${pin}`
      : `Check-in kiosk cho ${ev.name}\nLink: ${checkinUrl}\nMã PIN: ${pin}`;
    onCopy(msg, label);
  };

  const rotate = async () => {
    const confirmMsg = lang === "en"
      ? "Rotate PIN + link? Every active kiosk tablet will be signed out."
      : "Đổi mã PIN + link? Mọi kiosk đang mở sẽ bị đăng xuất.";
    if (!window.confirm(confirmMsg)) return;
    setRotating(true);
    try {
      const r = await api.post(
        `/admin/nail-events/${ev.slug}/kiosk-credentials/rotate`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      setCreds({
        pin: r.data.pin,
        link_token: r.data.link_token,
        checkin_url: r.data.checkin_url,
        rotated_at: r.data.rotated_at,
      });
      flash(lang === "en"
        ? `Rotated. ${r.data.sessions_revoked} kiosk session(s) signed out.`
        : `Đã đổi. Đã đăng xuất ${r.data.sessions_revoked} phiên kiosk.`);
      if (onRefresh) onRefresh();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    } finally {
      setRotating(false);
    }
  };

  return (
    <div
      data-testid={`kiosk-widget-${ev.slug}`}
      className="mt-4 rounded-2xl border border-espresso/10 bg-linen-100/50 p-3.5"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
          <KeyRound className="w-3 h-3" />
          {lang === "en" ? "Kiosk Access" : "Truy Cập Kiosk"}
        </div>
        {enabled ? (
          <span
            data-testid={`kiosk-enabled-${ev.slug}`}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide bg-[#E1EDE6] text-[#2D4A3E]"
          >
            <ShieldCheck className="w-3 h-3" />
            {lang === "en" ? "Enabled" : "Bật"}
          </span>
        ) : (
          <span
            data-testid={`kiosk-disabled-${ev.slug}`}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide bg-[#EAE5D9] text-espresso-soft"
          >
            <ShieldOff className="w-3 h-3" />
            {lang === "en" ? "Disabled" : "Tắt"}
          </span>
        )}
      </div>

      {/* Kiosk code */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted font-semibold">
            {lang === "en" ? "Code" : "Mã"}
          </div>
          <div
            data-testid={`kiosk-code-${ev.slug}`}
            className="font-mono text-sm font-semibold text-espresso truncate"
          >
            {code}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onCopy(code, lang === "en" ? "Kiosk code" : "Mã kiosk")}
          disabled={!ev.event_kiosk_code}
          data-testid={`copy-kiosk-code-${ev.slug}`}
          className="inline-flex items-center gap-1 border border-espresso/15 text-espresso-soft rounded-full px-2.5 py-1 text-[10px] font-medium hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Copy className="w-3 h-3" /> {lang === "en" ? "Copy" : "Sao chép"}
        </button>
      </div>

      {/* PIN */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted font-semibold">
            {lang === "en" ? "PIN" : "Mã PIN"}
          </div>
          <div
            data-testid={`kiosk-pin-${ev.slug}`}
            className="font-mono text-sm font-semibold text-espresso truncate"
          >
            {pin ? (revealed ? pin : "•".repeat(Math.min(pin.length, 6))) : (lang === "en" ? "Not set" : "Chưa cấu hình")}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            disabled={!pin}
            data-testid={`reveal-kiosk-pin-${ev.slug}`}
            className="inline-flex items-center gap-1 border border-espresso/15 text-espresso-soft rounded-full px-2.5 py-1 text-[10px] font-medium hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {revealed ? (lang === "en" ? "Hide" : "Ẩn") : (lang === "en" ? "Reveal" : "Hiện")}
          </button>
          <button
            type="button"
            onClick={rotate}
            disabled={rotating}
            data-testid={`rotate-kiosk-pin-${ev.slug}`}
            title={lang === "en" ? "New PIN, new link, signs out every active kiosk" : "PIN mới, link mới, đăng xuất mọi kiosk đang mở"}
            className="inline-flex items-center gap-1 border border-rose-accent/40 text-rose-accent rounded-full px-2.5 py-1 text-[10px] font-medium hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCw className={`w-3 h-3 ${rotating ? "animate-spin" : ""}`} />
            {lang === "en" ? "Rotate" : "Đổi mã"}
          </button>
        </div>
      </div>

      {/* Quick copy links */}
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={copyLinkAndPin}
          disabled={!pin || !linkToken}
          data-testid={`copy-kiosk-checkin-url-${ev.slug}`}
          className="inline-flex items-center gap-1 border border-espresso/15 text-espresso-soft rounded-lg px-2 py-1 text-[10px] font-medium hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed col-span-2"
        >
          <Copy className="w-3 h-3" /> {lang === "en" ? "Copy Check-in Link + PIN" : "Sao chép Link + PIN"}
        </button>
        <button
          type="button"
          onClick={() => onCopy(registrationUrl, lang === "en" ? "Registration URL" : "Link đăng ký")}
          data-testid={`copy-kiosk-registration-url-${ev.slug}`}
          className="inline-flex items-center gap-1 border border-espresso/15 text-espresso-soft rounded-lg px-2 py-1 text-[10px] font-medium hover:bg-white col-span-2"
        >
          <Copy className="w-3 h-3" /> {lang === "en" ? "Registration URL" : "Link đăng ký"}
        </button>
      </div>
    </div>
  );
}
